import React, { useState, useRef, useCallback, useEffect } from 'react';
import './styles/App.css';
import TopBar from './components/TopBar';
import CombatIntro from './components/CombatIntro';
import OmenModal from './components/OmenModal';
import LootModal from './components/LootModal';
import TacticalRadar from './components/TacticalRadar';
import SpellBook from './components/SpellBook';
import CampaignDrawer from './components/CampaignDrawer';
import SidePanel from './components/SidePanel';
import PlayerCard from './components/PlayerCard';
import EnemyPanel from './components/EnemyPanel';
import ActionBar from './components/ActionBar';
import Console from './components/Console';
import LightningVFX from './components/LightningVFX';
import MobileLayout from './components/MobileLayout';
import Setup from './components/Setup';
import LandingPage from './components/LandingPage';
import { playHealVFX } from './components/HealVFX';
import { useGameState } from './hooks/useGameState';
import { useCommand } from './hooks/useCommand';
import { useMediaQuery } from './hooks/useMediaQuery';
import { API_BASE } from './config';

export default function App() {
  // Restore theme from localStorage on mount
  React.useEffect(() => {
    const saved = localStorage.getItem('themeData');
    if (saved) {
      try { const t = JSON.parse(saved); document.documentElement.style.setProperty('--accent', t.accent); document.documentElement.style.setProperty('--bg', t.bg); document.documentElement.style.setProperty('--panel', t.panel); } catch {}
    }
  }, []);

  const { players, enemies, activeTurn, lastAttack, zones, banner: apiBanner, channels, pinnedChannels, combatSorting, campaignNotes, initialLoading, combatLog, combatLoot, setCombatLoot, combatActive, polledPlan, userRole } = useGameState();
  const { consoleLines, sendCommand, setConsoleLines } = useCommand();
  const [activeChannel, setActiveChannel] = useState('dm-tools');
  const [targeting, setTargeting] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [combatIntro, setCombatIntro] = useState(null);
  const [showOmen, setShowOmen] = useState(false);
  const [showRadar, setShowRadar] = useState(false);
  const [showSpellbook, setShowSpellbook] = useState(false);
  const prevEnemyCount = useRef(0);

  // Trigger combat intro when enemies first appear
  // Combat intro — disabled (set to true to re-enable)
  const COMBAT_INTRO_ENABLED = false;
  useEffect(() => {
    if (COMBAT_INTRO_ENABLED && enemies.length > 0 && prevEnemyCount.current === 0) {
      setCombatIntro(enemies[0].name);
    }
    prevEnemyCount.current = enemies.length;
  }, [enemies]);
  const [checkingTenant, setCheckingTenant] = useState(true);
  const [plan, setPlan] = useState('free');
  const isStandalone = !!localStorage.getItem('active_room');
  const currentUserId = (() => { try { const s = localStorage.getItem('session'); if (!s) return ''; const p = s.split('.')[1]; const pd = p + '='.repeat((4-p.length%4)%4); return JSON.parse(atob(pd.replace(/-/g,'+').replace(/_/g,'/'))).user_id || ''; } catch { return ''; } })();
  const isMyTurn = !activeTurn || activeTurn === currentUserId;  // true if no combat or it's my turn
  // Standalone free: basic combat UI enabled, advanced DM tools gated to Pro
  const isPro = plan === 'paid' || plan === 'pro' || plan === 'pro_plus';
  const isProPlus = plan === 'pro_plus';
  const hasBasicCombat = isStandalone || isPro;  // free standalone gets basic combat
  const inputRef = useRef(null);

  // Check if user has a registered tenant
  useEffect(() => {
    const session = localStorage.getItem('session');
    if (!session) { setCheckingTenant(false); return; }
    // Standalone users go to rooms, not tenant check
    try {
      const part = session.split('.')[1];
      const padded = part + '='.repeat((4 - part.length % 4) % 4);
      const payload = JSON.parse(atob(padded.replace(/-/g,'+').replace(/_/g,'/')));
      if (payload.mode === 'standalone') {
        const roomId = localStorage.getItem('active_room');
        if (roomId) {
          // Has an active room — load dashboard with room_id
          setPlan(payload.plan || 'free');
          setCheckingTenant(false);
          return;
        }
        // No active room — send to room lobby
        window.location.href = '/rooms';
        return;
      }
    } catch {}
    fetch(`${API_BASE}/tenant/me`, { headers: { 'Authorization': 'Bearer ' + session } })
      .then(r => r.json())
      .then(d => {
        if (!d.tenants || d.tenants.length === 0) setNeedsSetup(true);
        else setPlan(d.tenants[0].plan || 'free');
        setCheckingTenant(false);
      })
      .catch(() => setCheckingTenant(false));
  }, []);

  // Auto-trigger Stripe checkout if user came from "Subscribe" button
  useEffect(() => {
    if (checkingTenant) return;
    const pending = localStorage.getItem('pending_upgrade');
    const session = localStorage.getItem('session');
    if (pending && session) {
      const selectedPlan = localStorage.getItem('pending_plan') || 'pro';
      localStorage.removeItem('pending_upgrade');
      localStorage.removeItem('pending_plan');
      fetch(`${API_BASE}/billing/checkout`, { method: 'POST', headers: { 'Authorization': 'Bearer ' + session, 'Content-Type': 'application/json' }, body: JSON.stringify({ plan: selectedPlan }) })
        .then(r => r.json())
        .then(d => { if (d.checkout_url) window.location.href = d.checkout_url; });
    }
  }, [checkingTenant]);
  // Update plan from polled room data (standalone)
  useEffect(() => {
    if (isStandalone && polledPlan && polledPlan !== plan) setPlan(polledPlan);
  }, [polledPlan]);

  // Shared combat log — inject polled entries for all clients
  const lastSharedLogLen = useRef(0);
  const wasCombatActive = useRef(false);
  useEffect(() => {
    // Detect combat end via polling (for when another player ends it)
    if (wasCombatActive.current && !combatActive) {
      if (window._turnTimer) { clearTimeout(window._turnTimer); window._turnTimer = null; }
      setConsoleLines(prev => [...prev, { text: `🏳️ Combat ended.`, type: 'system' }]);
    }
    wasCombatActive.current = combatActive;

    if (combatLog.length > lastSharedLogLen.current) {
      const newEntries = combatLog.slice(lastSharedLogLen.current);
      if (window._logCleared) {
        // After clear, only show entries that arrive AFTER the clear
        lastSharedLogLen.current = combatLog.length;
        window._logCleared = false;
      } else {
        const existing = new Set(consoleLines.slice(-20).map(l => l.text));
        const filtered = newEntries.filter(l => !existing.has(l));
        if (filtered.length > 0) {
          setConsoleLines(prev => [...prev, ...filtered.map(l => ({ text: l, type: 'info' }))]);
        }
        lastSharedLogLen.current = combatLog.length;
      }
    }
  }, [combatLog, combatActive]);

  const isMobile = useMediaQuery('(max-width: 1024px)');

  const handleTarget = useCallback((charName) => {
    if (!targeting) return;
    if (targeting.trim() === '!dmheal') {
      const cards = document.querySelectorAll('.player-card');
      cards.forEach(card => {
        if (card.querySelector('.name') && card.querySelector('.name').textContent.includes(charName)) {
          playHealVFX(card);
        }
      });
      sendCommand(`!dmheal ${charName} 999`, activeChannel);
      setTargeting(null);
    } else if (targeting.trim() === '!give') {
      const item = prompt(`What item to give ${charName}?`);
      if (item) sendCommand(`!give ${charName} ${item}`, activeChannel);
      setTargeting(null);
    } else {
      if (inputRef.current) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
        nativeInputValueSetter.call(inputRef.current, targeting + charName + ' ');
        inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        inputRef.current.focus();
      }
      setTargeting(null);
    }
  }, [targeting, sendCommand, activeChannel]);

  // Secret command handler — banner is owner-only, writes directly to DynamoDB via API
  const handleCommand = (cmd, channel) => {
    if (cmd.startsWith('!banner ')) {
      const text = cmd.slice(8).trim();
      const session = localStorage.getItem('session');
      if (!session) return;
      try {
        const part = session.split('.')[1];
        const padded = part + '='.repeat((4 - part.length % 4) % 4);
        const payload = JSON.parse(atob(padded.replace(/-/g,'+').replace(/_/g,'/')));
        if (payload.user_id !== '254794024286814211') return;
        fetch(`${API_BASE}/state/${payload.guild_id}`, {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + session, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'set_banner', text: text === 'off' || text === 'clear' ? '' : text })
        });
      } catch(e) { console.error('Banner failed:', e); }
      return;
    }
    sendCommand(cmd, channel);
  };

  const handlePrefill = (cmd) => {
    if (inputRef.current) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      nativeInputValueSetter.call(inputRef.current, cmd);
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      inputRef.current.focus();
    }
  };

  if (checkingTenant) return <div style={{ background: 'var(--bg)', height: '100vh' }} />;

  // No session = show landing page
  if (!localStorage.getItem('session')) return <LandingPage />;

  if (needsSetup) {
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
        <TopBar channels={channels} pinnedChannels={pinnedChannels} campaignNotes={campaignNotes} plan={plan} />
        <Setup onComplete={() => { setNeedsSetup(false); window.location.reload(); }} />
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="app-mobile">
        <TopBar channels={channels} pinnedChannels={pinnedChannels} campaignNotes={campaignNotes} plan={plan} />
        <MobileLayout
          players={players}
          enemies={enemies}
          activeTurn={activeTurn}
          consoleLines={consoleLines}
          sendCommand={sendCommand}
          activeChannel={activeChannel}
          setActiveChannel={setActiveChannel}
          inputRef={inputRef}
          handleTarget={handleTarget}
          handlePrefill={handlePrefill}
          startTargeting={(cmd) => setTargeting(cmd)}
          targeting={targeting}
          isPro={isPro}
          isProPlus={isProPlus}
          hasBasicCombat={hasBasicCombat}
          isDM={!isStandalone || userRole === 'dm'}
          isMyTurn={isMyTurn}
          currentUserId={currentUserId}
        />
        <LightningVFX lastAttack={lastAttack} players={players} />
      </div>
    );
  }

  return (
    <div className="hud">
      <div style={{ position:'fixed',inset:0,background:'url(panopticon.gif) center/cover no-repeat',opacity:0.025,pointerEvents:'none',zIndex:0 }}/>
      <TopBar channels={channels} pinnedChannels={pinnedChannels} campaignNotes={campaignNotes} plan={plan} />
      {apiBanner && <div className="site-banner">{apiBanner}</div>}
      <div className="main-area">
        <div className={`player-panel${targeting ? ' targeting' : ''}`} id="player-panel">
          <div className="rune-circle-bg">
            <div className="panopticon-ring"></div>
            <div className="panopticon-ring-inner"></div>
            <div className="rune-ring">
              {'ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛞᛟ'.split('').map((r, i) => {
                const angle = (i / 24) * Math.PI * 2;
                const x = Math.cos(angle) * 200 + 204;
                const y = Math.sin(angle) * 200 + 204;
                return <span key={i} className="rune" style={{ left: x, top: y, animationDelay: `${i*0.2}s` }}>{r}</span>;
              })}
            </div>
          </div>
          {players.length === 0 ? (
            initialLoading ? (
              <div style={{padding:'40px',textAlign:'center',color:'var(--text-dim)',fontSize:'0.8rem'}}>Loading characters...</div>
            ) : (
              <div style={{ color: 'var(--text-dim)', padding: 40, textAlign: 'center', width: '100%' }}>
                {localStorage.getItem('session') ? 'No characters found. Create a character or run !sync in Discord.' : 'Login to view characters'}
              </div>
            )
          ) : (
            (combatSorting && activeTurn
              ? [players.find(p => p.discord_id === activeTurn), ...players.filter(p => p.discord_id !== activeTurn)].filter(Boolean)
              : players
            ).map(p => (
              <PlayerCard
                key={p.discord_id}
                player={p}
                isTurn={activeTurn === p.discord_id}
                onTarget={handleTarget}
                isDM={isStandalone}
                onKick={isStandalone ? (userId) => {
                  const token = localStorage.getItem('session');
                  const roomId = localStorage.getItem('active_room');
                  if (token && roomId) {
                    fetch(`${require('./config').API_BASE}/room/kick`, {
                      method: 'POST',
                      headers: {'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json'},
                      body: JSON.stringify({room_id: roomId, user_id: userId})
                    }).then(() => window.location.reload());
                  }
                } : null}
                onToggleActive={(name) => {
                  const cmd = p.active ? `!deactivate <@${p.discord_id}>` : `!activate <@${p.discord_id}>`;
                  sendCommand(cmd, 'dm-tools');
                }}
              />
            ))
          )}
        </div>
        <EnemyPanel enemies={enemies} />
      </div>
      <div className="console-area">
        {hasBasicCombat ? (
          <>
            <ActionBar
              onCommand={(cmd) => sendCommand(cmd, activeChannel)}
              onPrefill={handlePrefill}
              onStartTargeting={(cmd) => setTargeting(cmd)}
              onOpenRadar={() => setShowRadar(true)}
              onOpenSpellbook={() => setShowSpellbook(true)}
              isProPlus={isProPlus}
              isPro={isPro}
              hasBasicCombat={hasBasicCombat}
              isDM={!isStandalone || userRole === 'dm'}
              isMyTurn={isMyTurn}
            />
            <Console
              consoleLines={consoleLines}
              sendCommand={handleCommand}
              activeChannel={activeChannel}
              setActiveChannel={setActiveChannel}
              inputRef={inputRef}
              pinnedChannels={pinnedChannels}
              onReorderPinned={(newOrder) => {/* updates on next poll */}}
            />
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-dim)', fontSize: '0.85rem', flexDirection: 'column', gap: '12px' }}>
            <span>🔒 Web commands require a <strong style={{ color: 'var(--accent)' }}>paid plan</strong></span>
            <span style={{ fontSize: '0.7rem' }}>Character dashboard is available on all plans</span>
            <button className="btn-primary" onClick={async () => {
              const session = localStorage.getItem('session');
              const resp = await fetch(`${API_BASE}/billing/checkout`, { method: 'POST', headers: { 'Authorization': 'Bearer ' + session, 'Content-Type': 'application/json' } });
              const data = await resp.json();
              if (data.checkout_url) window.location.href = data.checkout_url;
            }}>⚡ Upgrade to Pro — $8/mo</button>
          </div>
        )}
      </div>
      <LightningVFX lastAttack={lastAttack} players={players} />
      {combatIntro && <CombatIntro enemyName={combatIntro} onComplete={() => setCombatIntro(null)} />}
      {showOmen && null}
      {showSpellbook && <SpellBook onClose={() => setShowSpellbook(false)} onCast={(name) => handleCommand(`!cast ${name}`, activeChannel)} />}
      {showRadar && <TacticalRadar
        zones={zones} 
        players={players}
        enemies={enemies}
        onMove={(name, zone) => sendCommand(`!move ${name} ${zone}`, 'combat')}
        onClose={() => setShowRadar(false)}
      />}
      <SidePanel campaignNotes={campaignNotes} onCommand={(cmd) => sendCommand(cmd, activeChannel)} isPro={isPro} isProPlus={isProPlus} players={players} isDM={!isStandalone || userRole === 'dm'} currentUserId={currentUserId} />
      {/* Loot displayed in console log instead of modal */}
    </div>
  );
}
